import Img from "components/common/Img";
import countryCodes from "components/dashboard/countryISOCodes.json";
import React from "react";
import ReactCountryFlag from "react-country-flag";
import { normalizeCountry } from "utils/countryNameAliases";
import ex from "../ExplorePage.module.scss";

// Shared atoms + constants for the Explore / Country / Destination pages.

export const MONTH_KEYS = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
export const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MONTH_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export const REGION_ORDER = ["Europe", "Asia", "Americas", "Africa", "Oceania"];

// Buckets keyed off destinationCount (already fetched per country) as a proxy for suggested trip length —
// countries with few published destinations suit a quick trip, more destinations warrant more time.
export const TRIP_LENGTHS: { key: string; label: string; min: number; max: number }[] = [
	{ key: "weekend", label: "Weekend", min: 0, max: 3 },
	{ key: "1week", label: "1 week", min: 4, max: 6 },
	{ key: "2weeks", label: "2 weeks", min: 7, max: 12 },
	{ key: "3weeks+", label: "3+ weeks", min: 13, max: Infinity },
];

// Month display labels localized to the active language (data keys stay English — MONTH_KEYS).
export const localizedMonths = (lang: string, type: "short" | "long"): string[] => {
	try {
		return Array.from({ length: 12 }, (_, i) => new Date(2021, i, 1).toLocaleString(lang || "en", { month: type }));
	} catch (e) {
		return type === "short" ? MONTH_SHORT : MONTH_FULL;
	}
};

// 16 vibe vocabulary → friendly label + emoji stand-in (Figma icon set deferred).
export const VIBE_META: Record<string, { label: string; icon: string }> = {
	adventure: { label: "Adventure", icon: "🧗" },
	arts: { label: "Arts & Culture", icon: "🎨" },
	beach: { label: "Beach", icon: "🏖️" },
	culinary: { label: "Food & Drink", icon: "🍽️" },
	festival: { label: "Festivals", icon: "🎉" },
	historic: { label: "History", icon: "🏛️" },
	nature: { label: "Nature", icon: "🌿" },
	nightlife: { label: "Nightlife", icon: "🌃" },
	off_the_beaten_path: { label: "Off the Beaten Path", icon: "🧭" },
	romantic: { label: "Romantic", icon: "💕" },
	scenic: { label: "Scenic", icon: "🏞️" },
	ski: { label: "Skiing", icon: "🎿" },
	spiritual: { label: "Spiritual", icon: "🧘" },
	village: { label: "Villages", icon: "🏘️" },
	wellness: { label: "Wellness", icon: "💆" },
	wildlife: { label: "Wildlife", icon: "🦁" },
};

// Weather icon stand-ins (Figma icon set deferred).
export const WEATHER_ICON: Record<string, string> = {
	sun: "☀️",
	partly_cloudy: "⛅",
	cloud: "☁️",
	rain: "🌧️",
	snow: "❄️",
};

export const prettify = (slug: string) =>
	(slug || "")
		.split(/[-_]/)
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
		.join(" ");

export { COUNTRY_NAME_ALIASES, normalizeCountry } from "utils/countryNameAliases";
export const alpha2For = (name: string): string | undefined => {
	const normalized = normalizeCountry(name).toLowerCase();
	return (countryCodes as any[]).find((c: any) => c.country.toLowerCase() === normalized)?.alpha2;
};

export const isoEntry = (name: string): any => (countryCodes as any[]).find((c: any) => c.country === normalizeCountry(name)) || {};

export const regionOf = (name: string): string => isoEntry(name).region || "Other";

// --- Display-only name localization (DATA keys — slug, name, image/search lookups — stay English) ---
// Country names use the browser's Intl region table (reliable for every country, zero cost). City
// names have no such table, so a small curated exonym map covers the majors; everything else falls
// back to its original name. Only ever used at render sites — never routed through data/trip creation.
const regionDisplayNamesCache: Record<string, Intl.DisplayNames | null> = {};
const regionDisplayNames = (lang: string): Intl.DisplayNames | null => {
	if (!(lang in regionDisplayNamesCache)) {
		try {
			regionDisplayNamesCache[lang] = new Intl.DisplayNames([lang], { type: "region" });
		} catch {
			regionDisplayNamesCache[lang] = null;
		}
	}
	return regionDisplayNamesCache[lang];
};

export const displayCountryName = (name: string, lang?: string): string => {
	const code = (lang || "en").split("-")[0];
	if (!name || code === "en") return name;
	const alpha2 = alpha2For(name);
	if (!alpha2) return name;
	try {
		return regionDisplayNames(code)?.of(alpha2) || name;
	} catch {
		return name;
	}
};

// Curated exonyms for the ~35 major cities whose name genuinely localizes (Paris→Parijs, Munich→München).
// Keyed by the exact English `name` in the dataset; a language is listed only when it differs from English.
export const CITY_EXONYMS: Record<string, Partial<Record<string, string>>> = {
	Paris: { nl: "Parijs", es: "París", it: "Parigi" },
	Rome: { de: "Rom", es: "Roma", it: "Roma", pt: "Roma" },
	Venice: { nl: "Venetië", de: "Venedig", fr: "Venise", es: "Venecia", it: "Venezia", pt: "Veneza" },
	Florence: { de: "Florenz", es: "Florencia", it: "Firenze", pt: "Florença" },
	Milan: { nl: "Milaan", de: "Mailand", es: "Milán", it: "Milano", pt: "Milão" },
	Naples: { nl: "Napels", de: "Neapel", es: "Nápoles", it: "Napoli", pt: "Nápoles" },
	Turin: { nl: "Turijn", es: "Turín", it: "Torino", pt: "Turim" },
	Genoa: { nl: "Genua", de: "Genua", fr: "Gênes", es: "Génova", it: "Genova", pt: "Génova" },
	Munich: { nl: "München", de: "München", es: "Múnich", it: "Monaco di Baviera", pt: "Munique" },
	Cologne: { nl: "Keulen", de: "Köln", es: "Colonia", it: "Colonia", pt: "Colónia" },
	Nuremberg: { nl: "Neurenberg", de: "Nürnberg", es: "Núremberg", it: "Norimberga", pt: "Nuremberga" },
	Vienna: { nl: "Wenen", de: "Wien", fr: "Vienne", es: "Viena", it: "Vienna", pt: "Viena" },
	Prague: { nl: "Praag", de: "Prag", es: "Praga", it: "Praga", pt: "Praga" },
	Warsaw: { nl: "Warschau", de: "Warschau", fr: "Varsovie", es: "Varsovia", it: "Varsavia", pt: "Varsóvia" },
	Krakow: { nl: "Krakau", de: "Krakau", fr: "Cracovie", es: "Cracovia", it: "Cracovia", pt: "Cracóvia" },
	Moscow: { nl: "Moskou", de: "Moskau", fr: "Moscou", es: "Moscú", it: "Mosca", pt: "Moscovo" },
	"Saint Petersburg": { nl: "Sint-Petersburg", de: "Sankt Petersburg", fr: "Saint-Pétersbourg", es: "San Petersburgo", it: "San Pietroburgo", pt: "São Petersburgo" },
	Lisbon: { nl: "Lissabon", de: "Lissabon", fr: "Lisbonne", es: "Lisboa", it: "Lisbona", pt: "Lisboa" },
	Seville: { nl: "Sevilla", de: "Sevilla", fr: "Séville", es: "Sevilla", it: "Siviglia", pt: "Sevilha" },
	Athens: { nl: "Athene", de: "Athen", fr: "Athènes", es: "Atenas", it: "Atene", pt: "Atenas" },
	Copenhagen: { nl: "Kopenhagen", de: "Kopenhagen", fr: "Copenhague", es: "Copenhague", it: "Copenaghen", pt: "Copenhaga" },
	Brussels: { nl: "Brussel", de: "Brüssel", fr: "Bruxelles", es: "Bruselas", it: "Bruxelles", pt: "Bruxelas" },
	Antwerp: { nl: "Antwerpen", de: "Antwerpen", fr: "Anvers", es: "Amberes", it: "Anversa", pt: "Antuérpia" },
	Bruges: { nl: "Brugge", de: "Brügge", es: "Brujas" },
	Geneva: { nl: "Genève", de: "Genf", fr: "Genève", es: "Ginebra", it: "Ginevra", pt: "Genebra" },
	Basel: { nl: "Bazel", fr: "Bâle", es: "Basilea", it: "Basilea", pt: "Basileia" },
	Bucharest: { nl: "Boekarest", de: "Bukarest", fr: "Bucarest", es: "Bucarest", it: "Bucarest", pt: "Bucareste" },
	Belgrade: { nl: "Belgrado", de: "Belgrad", es: "Belgrado", it: "Belgrado", pt: "Belgrado" },
	Cairo: { nl: "Caïro", de: "Kairo", fr: "Le Caire", es: "El Cairo", it: "Il Cairo" },
	Jerusalem: { nl: "Jeruzalem", fr: "Jérusalem", es: "Jerusalén", it: "Gerusalemme", pt: "Jerusalém" },
	Beijing: { nl: "Peking", de: "Peking", fr: "Pékin", es: "Pekín", it: "Pechino", pt: "Pequim" },
	Tokyo: { nl: "Tokio", de: "Tokio", es: "Tokio", pt: "Tóquio" },
	Strasbourg: { nl: "Straatsburg", de: "Straßburg", es: "Estrasburgo", it: "Strasburgo", pt: "Estrasburgo" },
	"The Hague": { nl: "Den Haag", de: "Den Haag", fr: "La Haye", es: "La Haya", it: "L'Aia", pt: "Haia" },
	Nice: { de: "Nizza", es: "Niza", it: "Nizza" },
	Marseille: { es: "Marsella", it: "Marsiglia", pt: "Marselha" },
	Lyon: { it: "Lione" },
	Berlin: { nl: "Berlijn", es: "Berlín", it: "Berlino", pt: "Berlim" },
	Hamburg: { fr: "Hambourg", es: "Hamburgo", it: "Amburgo", pt: "Hamburgo" },
	Kyoto: { es: "Kioto", pt: "Quioto" },
	Seoul: { nl: "Seoel", fr: "Séoul", es: "Seúl", it: "Seul", pt: "Seul" },
	Hanoi: { fr: "Hanoï", es: "Hanói", pt: "Hanói" },
	"Ho Chi Minh City": { nl: "Ho Chi Minhstad", de: "Ho-Chi-Minh-Stadt", fr: "Hô Chi Minh-Ville", es: "Ciudad Ho Chi Minh", it: "Città di Ho Chi Minh", pt: "Cidade de Ho Chi Minh" },
	"Mexico City": { nl: "Mexico-Stad", de: "Mexiko-Stadt", fr: "Mexico", es: "Ciudad de México", it: "Città del Messico", pt: "Cidade do México" },
	"Panama City": { nl: "Panama-Stad", de: "Panama-Stadt", fr: "Panama", es: "Ciudad de Panamá", it: "Città di Panama", pt: "Cidade do Panamá" },
	Dubai: { fr: "Dubaï", es: "Dubái" },
};

export const displayCityName = (name: string, lang?: string): string => {
	const code = (lang || "en").split("-")[0];
	if (!name || code === "en") return name;
	return CITY_EXONYMS[name]?.[code] || name;
};

// Split a "Title (description)" or "Title — description" string into parts.
export const parseTitleDesc = (raw: string): { title: string; desc?: string } => {
	const s = (raw || "").trim();
	const dash = s.match(/^(.*?)\s+[—–-]\s+(.+)$/);
	if (dash) return { title: dash[1].trim(), desc: dash[2].trim() };
	const paren = s.match(/^(.*?)\s*\((.+)\)\s*$/);
	if (paren) return { title: paren[1].trim(), desc: paren[2].trim() };
	return { title: s };
};

// Circular flag chip — always clips to a circle; caller's className adds border/shadow.
// `alt` defaults to "": every call site sits beside the country/place name in text (rendered
// as an h1 immediately above it on the destination and country pages), so the flag is
// decorative — an `<img>` with no `alt` at all falls back to the filename for a screen reader,
// while `alt=""` correctly drops it from the accessibility tree instead of announcing the name
// twice.
export const CircleFlag: React.FC<{ country: string; className?: string; size: number; alt?: string }> = ({ country, className, size, alt = "" }) => {
	const iso = isoEntry(country).alpha2;
	return (
		<span className={className} style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden" }}>
			{iso && (
				<ReactCountryFlag
					svg
					countryCode={iso}
					alt={alt}
					style={{ width: "100%", height: "100%", objectFit: "cover", verticalAlign: "unset", display: "block" }}
				/>
			)}
		</span>
	);
};

// Clipped image that self-fetches a photo for a country (and optional city/place).
// Pass `src` to use a curated image (e.g. ExploreCountry.heroImage) instead of the live search.
export const PhotoFill: React.FC<{ country: string; city?: string; src?: string }> = ({ country, city, src }) => (
	<div className={ex.fillImg} style={{ position: "absolute", inset: 0 }}>
		<Img src={src} country={country} city={city} type="regular" caption={false} loadtype="lazy" />
	</div>
);
